import { Attr, BelongsTo, HasMany, Model } from "spraypaint";

import { ApplicationRecord } from "shared/services/spraypaint";
import type { Nullable } from "shared/types";

import type AIVisibilityProject from "./AIVisibilityProject";
import type Organisation from "./Organisation";
import type User from "./User";

// Trajaan's project-scoped credits response, stored verbatim so an admin can
// inspect quotas we do not give columns to (e.g. "ai_report").
//
// Note the vendor semantics: a usage entry's total_credits is credits
// CONSUMED, not a ceiling. Ceilings live in company_credits.credits (base pool)
// and quotas[].credits (per-quota), which is why the flattened columns pair
// each ceiling with its own usage entry rather than reading both from one.
interface AIVisibilityCompanyCreditUsage {
  quota_id?: string;
  remaining_credits: Nullable<number>;
  total_credits: Nullable<number>;
}

interface AIVisibilityCompanyQuota {
  credits: Nullable<number>;
  quota_id: string;
}

export interface AIVisibilityCompanyCreditsRaw {
  company_credits?: {
    credits: Nullable<number>;
    credits_consumed: Nullable<number>;
    end_date: Nullable<string>;
    is_active: Nullable<boolean>;
    start_date: Nullable<string>;
  };
  global_usage?: AIVisibilityCompanyCreditUsage;
  quota_usage?: AIVisibilityCompanyCreditUsage[];
  quotas?: AIVisibilityCompanyQuota[];
}

@Model()
class AIVisibilityCompany extends ApplicationRecord {
  static apiNamespace = "/ai";

  static endpoint = "/visibility_companies";

  static jsonapiType = "ai_visibility_companies";

  @Attr({ persist: false }) activeProjectsCount: number;

  @Attr({ persist: false }) archived: boolean;

  @Attr({ persist: false }) archivedAt: Nullable<string>;

  @Attr({ persist: false }) companyName: Nullable<string>;

  @Attr({ persist: false }) companySlug: Nullable<string>;

  @Attr({ persist: false }) createdAt: string;

  @BelongsTo() creator: User;

  @Attr({ persist: false }) creatorId: number;

  @Attr({ persist: false }) creditsConsumed: Nullable<number>;

  // Non-null when the most recent refresh failed. The stored figures and
  // creditsSyncedAt are left untouched on failure, so this coexists with a
  // populated (but stale) credit picture rather than replacing it.
  @Attr({ persist: false }) creditsError: Nullable<string>;

  @Attr({ persist: false }) creditsPeriodEnd: Nullable<string>;

  @Attr({ persist: false }) creditsPeriodStart: Nullable<string>;

  @Attr({ persist: false })
  creditsRaw: Nullable<AIVisibilityCompanyCreditsRaw>;

  @Attr({ persist: false }) creditsRemaining: Nullable<number>;

  @Attr({ persist: false }) creditsSubscriptionActive: Nullable<boolean>;

  // Last SUCCESSFUL sync, not last attempt.
  @Attr({ persist: false }) creditsSyncedAt: Nullable<string>;

  @Attr({ persist: false }) creditsTotal: Nullable<number>;

  @Attr({ persist: false }) externalCustomerId: Nullable<string>;

  @Attr({ persist: false }) genaiCreditsConsumed: Nullable<number>;

  @Attr({ persist: false }) genaiCreditsRemaining: Nullable<number>;

  @Attr({ persist: false }) genaiCreditsTotal: Nullable<number>;

  // The API key itself is never serialised — a revoked (archival nulls the
  // ciphertext) or never-provisioned key is what this surfaces.
  @Attr({ persist: false }) hasApiKey: boolean;

  @Attr({ persist: false }) industry: Nullable<string>;

  @BelongsTo() organisation: Organisation;

  @Attr({ persist: false }) organisationId: number;

  @HasMany() projects: AIVisibilityProject[];

  @Attr({ persist: false }) trajaanCompanyId: number;

  @Attr({ persist: false }) updatedAt: string;
}

export default AIVisibilityCompany;
